import React, { useState } from 'react'
import {
  Box,
  TextField,
  FormHelperText,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogContent,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
import UploadIcon from '@mui/icons-material/Upload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { state } from '../../../constant/state'
import { product } from '../../../constant/product'
import {
  useGetEndUserScratchCardCanSubmit,
  usePostEndUserScratchCard,
} from '../../../api/promotions/end-user/end-user-scratch-card'
import { useNavigate } from 'react-router-dom'

const MAX_FILE_SIZE = 5 // MB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg']

const StyledFieldBox = styled(Box)`
  && {
    max-width: 800px;
    justify-content: space-evenly;
    min-height: 80px;
    margin: auto;
    display: flex;
    flex-direction: column;
  }
`

const ScratchCardForm = () => {
  const { register, handleSubmit, control, setValue, setError } = useForm({
    mode: 'onChange',
  })

  const navigate = useNavigate()
  const { mutateAsync, isLoading } = usePostEndUserScratchCard()

  const { data } = useGetEndUserScratchCardCanSubmit()

  const { enqueueSnackbar } = useSnackbar()

  const [openDialog, setopenDialog] = useState(false)

  const onSubmit = async (data) => {
    let formData = new FormData()

    for (let key in data) {
      // Add files
      if (['batteryInvoice', 'batteryPhoto', 'scratchCardPhoto'].includes(key)) {
        formData.append(key, data[key][0])
      } else {
        formData.append(key, data[key])
      }
    }

    try {
      const res = await mutateAsync(formData)

      setopenDialog(true)

      return res
    } catch (error) {
      // Display error on popup
      enqueueSnackbar(error?.response?.data?.message ?? 'Submit fail', {
        variant: 'error',
        autoHideDuration: 7000,
      })
    }
  }

  const handleClose = () => {
    // Close dialog
    setopenDialog(false)

    // Navigate back to home page
    navigate('/')

    // Scroll back to top
    window.scrollTo(0, 0)
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledFieldBox>
          <Controller
            name='name'
            control={control}
            defaultValue=''
            rules={{
              required: 'Name is required',
              maxLength: { value: 200, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Name'
                fullWidth
                value={value}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='phone'
            control={control}
            defaultValue=''
            rules={{
              required: 'Phone number is required',
              pattern: {
                value: /^(601)[0-46-9]-*[0-9]{7,8}$/,
                message: `Invalid phone number. Phone number should follow the format of 601xxxxxxxx. '+' sign is not required.`,
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Phone number'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                placeholder='60127123456'
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='email'
            control={control}
            defaultValue=''
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
              maxLength: { value: 50, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label='Email (Optional)'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='state'
            control={control}
            rules={{
              required: 'State is required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                options={state}
                onChange={(e, data) => onChange(data)}
                value={value || ''}
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                    label='State'
                  />
                )}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='workshop'
            control={control}
            defaultValue=''
            render={({ field: { onChange, value } }) => (
              <TextField label='Workshop Name' fullWidth value={value} onChange={onChange} />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='brand'
            control={control}
            rules={{
              required: 'Brand is required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                options={product}
                onChange={(e, data) => onChange(data)}
                value={value || ''}
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                    label='Brand'
                  />
                )}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='serialNumber'
            control={control}
            defaultValue=''
            rules={{
              required: 'Battery Serial number is required',
              pattern: {
                value: /^[VE]([YZ]\d{7}|[01]\d{6})$/i, // case insensitive
                message:
                  'Invalid battery serial number format - VYxxxxxxx / EYxxxxxxx / VZxxxxxxx / EZxxxxxxx / V0xxxxxx / E0xxxxxx / V1xxxxxx / E1xxxxxx',
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const handleChange = (event) => {
                const sanitizedValue = event.target.value.replace(/\s/g, '').toUpperCase()
                onChange(sanitizedValue)
              }
              return (
                <TextField
                  label='Battery Serial Number'
                  value={value}
                  onChange={handleChange}
                  error={!!error}
                  fullWidth
                  helperText={error ? error.message : null}
                />
              )
            }}
          />
        </StyledFieldBox>
        <StyledFieldBox>
          <Controller
            name='type'
            control={control}
            rules={{
              required: 'Type is required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                options={[
                  'RM 10 TNG Reload PIN and a chance to win a GWM Haval H6',
                  'A chance to win a GWM Haval H6',
                ]}
                onChange={(e, data) => onChange(data)}
                value={value || ''}
                renderInput={(params) => (
                  <TextField
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                    label='Type'
                  />
                )}
              />
            )}
          />
        </StyledFieldBox>
        <StyledFieldBox display='flex' flexDirection='column'>
          <Box>
            <Controller
              name='batteryInvoice'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('batteryInvoice')
                // React Hook Form does not block field value being set on failing validation
                // This is a workaround by manually handling both the file size validation and value assignment
                const handleOnChange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2

                  const fileType = e?.target?.files[0]?.type

                  let errorMsg = null

                  // Validate file size
                  if (fileMb > MAX_FILE_SIZE) {
                    // Set error message
                    errorMsg = 'File size must be 5 MB or smaller'
                  } else if (!ALLOWED_FILE_TYPES.includes(fileType)) {
                    // Validate file type
                    errorMsg = 'Only PNG and JPG files are allowed'
                  }

                  // If there are errors
                  if (errorMsg) {
                    // Set error
                    setError(props.name, {
                      type: 'manual',
                      message: errorMsg,
                    })

                    // Reset field value
                    setValue(props.name, null)
                  } else {
                    // Set field value
                    props.onChange(e)
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload a receipt / invoice
                      <input
                        type='file'
                        accept='image/*'
                        hidden
                        {...props}
                        onChange={handleOnChange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>
        <StyledFieldBox display='flex' flexDirection='column'>
          {/* Sample for users to used as reference */}
          <Typography variant='h2' mt={2} mb={2}>
            Sample of the installed battery (must show the battery serial number)
          </Typography>
          <img
            src='/assets/promotions/end-user/installed-battery/sample.png'
            style={{ maxWidth: '200px' }}
          />
          <Box mt={4}>
            <Controller
              name='batteryPhoto'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('batteryPhoto')
                // React Hook Form does not block field value being set on failing validation
                // This is a workaround by manually handling both the file size validation and value assignment
                const handleOnChange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2

                  const fileType = e?.target?.files[0]?.type

                  let errorMsg = null

                  // Validate file size
                  if (fileMb > MAX_FILE_SIZE) {
                    // Set error message
                    errorMsg = 'File size must be 5 MB or smaller'
                  } else if (!ALLOWED_FILE_TYPES.includes(fileType)) {
                    // Validate file type
                    errorMsg = 'Only PNG and JPG files are allowed'
                  }

                  // If there are errors
                  if (errorMsg) {
                    // Set error
                    setError(props.name, {
                      type: 'manual',
                      message: errorMsg,
                    })

                    // Reset field value
                    setValue(props.name, null)
                  } else {
                    // Set field value
                    props.onChange(e)
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload a photo of the installed battery in the car
                      <input
                        type='file'
                        accept='image/*'
                        hidden
                        {...props}
                        onChange={handleOnChange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>

        <StyledFieldBox display='flex' flexDirection='column'>
          {/* Sample for users to used as reference */}
          <Typography variant='h2' mt={2} mb={2}>
            Sample of the scratch card
          </Typography>
          <img
            src='/assets/promotions/end-user/scratch-card-redemption/sample.png'
            style={{ maxWidth: '400px' }}
          />
          <Box mt={4}>
            <Controller
              name='scratchCardPhoto'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('scratchCardPhoto')

                // React Hook Form does not block field value being set on failing validation
                // This is a workaround by manually handling both the file size validation and value assignment
                const handleOnChange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2

                  const fileType = e?.target?.files[0]?.type

                  let errorMsg = null

                  // Validate file size
                  if (fileMb > MAX_FILE_SIZE) {
                    // Set error message
                    errorMsg = 'File size must be 5 MB or smaller'
                  } else if (!ALLOWED_FILE_TYPES.includes(fileType)) {
                    // Validate file type
                    errorMsg = 'Only PNG and JPG files are allowed'
                  }

                  // If there are errors
                  if (errorMsg) {
                    // Set error
                    setError(props.name, {
                      type: 'manual',
                      message: errorMsg,
                    })

                    // Reset field value
                    setValue(props.name, null)
                  } else {
                    // Set field value
                    props.onChange(e)
                  }
                }

                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload a photo of the scratch card
                      <input
                        type='file'
                        accept='image/*'
                        hidden
                        {...props}
                        onChange={handleOnChange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                  </>
                )
              }}
            />
          </Box>
        </StyledFieldBox>
        <StyledFieldBox>
          <Box my={2}>
            <Typography variant='h4'>
              * Please make sure your phone number is the same as your Touch’n Go e-Wallet account.
            </Typography>
            <Typography variant='h4'>
              * Redemption of one (1) Touch'N Go reload pin number is strictly limited to one (1) HT
              battery serial number.
            </Typography>
            <Typography variant='h4'>
              * Purchase any Energizer® or VARTA® automotive batteries from participating dealers
              from Feb 2026 are allowed to redeem the scratch card prize or Touch’nGo reload pin by
              31/5/2026.
            </Typography>
            <Typography variant='h4'>
              * The HT battery serial number must match in the HT Eco Battery Sdn Bhd system. If the
              provided serial number is not found in the system, it will be considered ineligible
              for the redemption of the Touch'N Go reload pin number.
            </Typography>
            <Typography variant='h4'>
              * By submitting this form, you confirm that you have read, understood, and will
              respect the{' '}
              <a
                style={{ color: '#002984' }}
                href={'/assets/promotions/end-user/scratch-card-redemption/TermsAndConditions.pdf'}
                target='_blank'
                rel='noopener noreferrer'
              >
                Terms & Conditions
              </a>{' '}
              &{' '}
              <a
                style={{ color: '#002984' }}
                href={'/assets/promotions/end-user/scratch-card-redemption/PrivacyPolicy.pdf'}
                target='_blank'
                rel='noopener noreferrer'
              >
                Privacy Policy.
              </a>
            </Typography>
          </Box>
          <Box>
            <Button
              fullWidth
              type='submit'
              size='large'
              variant='contained'
              disabled={isLoading || !data?.allow}
              endIcon={isLoading && <CircularProgress style={{ color: '#fff' }} size='1rem' />}
            >
              Submit
            </Button>
            <Typography variant='body2' color='error' mt={1}>
              {(() => {
                // If not allowed, return the message
                if (!data?.allow) return data?.message
                else if (isLoading) return 'Please wait while we process your submission.'
                else return null
              })()}
            </Typography>
          </Box>
        </StyledFieldBox>
      </form>
      <Dialog open={openDialog} onClose={handleClose}>
        <DialogContent dividers>
          <Box display='flex' justifyContent='flex-start' alignItems='center' pb={3}>
            <CheckCircleIcon style={{ color: '#4caf50' }} />
            <Typography variant={'h3'}>{'Form Submitted.'}</Typography>
          </Box>
          <Typography gutterBottom>Redirecting back to homepage...</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default ScratchCardForm
